// D-067 (BILLING-001): one Adyvance invoice to a shop, the same in the owner console and the vendor
// admin. The issuer is the owner's company data (empty until set: the invoice says so, nothing is
// made up). Print prints only the invoice (a copy at the end of <body>, `.ady-invoice-print`).
import { createPortal } from "react-dom";
import { formatDateAD, formatMoney } from "@ady/format";
import { t } from "@ady/i18n";
import { Button } from "./Button";
import { Logo } from "./Logo";

export interface InvoiceDocumentData {
  invoice: {
    number: string;
    issued_at: string | null;
    amount_paisa: number;
    paid_via: string | null;
  };
  issuer: {
    legal_name: string;
    pan_vat: string;
    address: string;
    phone: string;
    email: string;
    note: string;
  };
  customer: { name: string; legal_name: string | null; pan_vat: string | null };
  lines: { label: string; amount_paisa: number }[];
}

function Sheet({ d }: { d: InvoiceDocumentData }) {
  const i = d.issuer;
  const contact = [i.phone, i.email].filter(Boolean).join(" · ");
  return (
    <article className="flex flex-col gap-4 text-sm" data-testid="invoice-document">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <Logo height={26} tone="color" />
        <span className="flex flex-col items-end">
          <span className="text-title-sm font-bold">{t("billingOps.doc.title")}</span>
          <span className="text-xs text-fg-secondary">
            {t("billingOps.doc.number", { number: d.invoice.number })}
          </span>
          <span className="text-xs text-fg-secondary">
            {t("billingOps.doc.date")}:{" "}
            {d.invoice.issued_at ? formatDateAD(d.invoice.issued_at) : ""}
          </span>
        </span>
      </header>
      <div className="grid gap-3 sm:grid-cols-2">
        <section className="flex flex-col gap-0.5">
          <span className="text-2xs font-bold tracking-wide text-fg-tertiary uppercase">
            {t("billingOps.doc.from")}
          </span>
          {i.legal_name ? (
            <>
              <span className="font-semibold">{i.legal_name}</span>
              {i.pan_vat ? (
                <span className="text-xs">{t("billingOps.doc.pan", { pan: i.pan_vat })}</span>
              ) : null}
              {i.address ? <span className="text-xs whitespace-pre-line">{i.address}</span> : null}
              {contact ? <span className="text-xs">{contact}</span> : null}
            </>
          ) : (
            <span className="text-xs text-warning-fg">{t("billingOps.doc.noIssuer")}</span>
          )}
        </section>
        <section className="flex flex-col gap-0.5">
          <span className="text-2xs font-bold tracking-wide text-fg-tertiary uppercase">
            {t("billingOps.doc.billTo")}
          </span>
          <span className="font-semibold">{d.customer.legal_name ?? d.customer.name}</span>
          {d.customer.legal_name ? <span className="text-xs">{d.customer.name}</span> : null}
          {d.customer.pan_vat ? (
            <span className="text-xs">{t("billingOps.doc.pan", { pan: d.customer.pan_vat })}</span>
          ) : null}
        </section>
      </div>
      <table className="w-full text-sm">
        <thead className="border-b border-subtle text-xs text-fg-tertiary">
          <tr>
            <th className="py-1.5 text-start font-semibold">{t("billingOps.doc.item")}</th>
            <th className="py-1.5 text-end font-semibold">{t("billingOps.doc.amount")}</th>
          </tr>
        </thead>
        <tbody>
          {d.lines.map((l, n) => (
            <tr key={n} className="border-b border-subtle">
              <td className="py-1.5">{l.label}</td>
              <td className="py-1.5 text-end tabular-nums">{formatMoney(l.amount_paisa)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td className="pt-2 font-bold">{t("billingOps.doc.total")}</td>
            <td className="pt-2 text-end font-bold tabular-nums">
              {formatMoney(d.invoice.amount_paisa)}
            </td>
          </tr>
        </tfoot>
      </table>
      <footer className="flex flex-col gap-0.5 text-xs text-fg-secondary">
        {d.invoice.paid_via === "wallet" ? <span>{t("billingOps.doc.paidVia")}</span> : null}
        {i.note ? <span className="whitespace-pre-line">{i.note}</span> : null}
      </footer>
    </article>
  );
}

export function InvoiceDocument({
  data,
  onClose,
}: {
  data: InvoiceDocumentData;
  onClose?: () => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <Sheet d={data} />
      <div className="flex justify-end gap-1.5 border-t border-subtle pt-3">
        {onClose ? (
          <Button variant="secondary" onClick={onClose}>
            {t("billingOps.doc.close")}
          </Button>
        ) : null}
        <Button icon="printer" onClick={() => window.print()} data-testid="invoice-print">
          {t("billingOps.doc.print")}
        </Button>
      </div>
      {typeof document !== "undefined"
        ? createPortal(
            <div className="ady-invoice-print" aria-hidden="true">
              <Sheet d={data} />
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
